import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormHelperText,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Pagination,
    Paper,
    Select,
    Stack,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import {
    Add as AddIcon,
    ContentCopy as CopyIcon,
    Delete as DeleteIcon,
    Forum as ForumIcon,
    NotificationsActive as NotificationsIcon,
    Refresh as RefreshIcon,
    Search as SearchIcon,
    Visibility as VisibilityIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const STATUS_TABS = [
    { code: 'submitted', label: 'Submitted' },
    { code: 'pending', label: 'Pending' },
    { code: 'completed', label: 'Complete' }
];

const STATUS_COLORS = {
    submitted: 'info',
    pending: 'warning',
    completed: 'success'
};

const VANCOUVER_TZ = 'America/Vancouver';
const DETAILS_MIN = 200;
const DETAILS_MAX = 5000;
const COMMENT_MAX = 3000;
const ESCALATION_TEXT_SX = {
    maxWidth: '100%',
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word'
};

function currentVancouverMonth() {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: VANCOUVER_TZ,
        year: 'numeric',
        month: '2-digit'
    }).formatToParts(new Date());
    const year = parts.find((part) => part.type === 'year')?.value;
    const month = parts.find((part) => part.type === 'month')?.value;
    return `${year}-${month}`;
}

function formatDateTime(value) {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: VANCOUVER_TZ,
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: 'numeric',
        minute: '2-digit'
    }).format(date);
}

function ageLabel(value) {
    if (!value) return '-';
    const start = new Date(value).getTime();
    if (Number.isNaN(start)) return '-';
    const diffMs = Date.now() - start;
    const hours = Math.floor(diffMs / 3600000);
    if (hours < 1) return 'Less than 1h';
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h`;
}

function statusChip(status) {
    const tab = STATUS_TABS.find((item) => item.code === status);
    return <Chip size="small" color={STATUS_COLORS[status] || 'default'} label={tab?.label || status} />;
}

function exactEscalationSearch(value) {
    return /^ESC-\d{4}-[A-Z0-9]{6}$/i.test(String(value || '').trim());
}

function getCount(summary, status) {
    return Number(summary?.statusCounts?.[status] || 0);
}

function getTabCount(summary, status) {
    if (status === 'submitted') return getCount(summary, status);
    return Number(summary?.currentMonthCounts?.[status] || 0);
}

function fieldValue(value) {
    return String(value || '').trim();
}

function validateEscalationForm(values) {
    const validationErrors = {};
    const detailsLength = fieldValue(values.details).length;

    if (!fieldValue(values.customerFullName)) validationErrors.customerFullName = 'Customer full name is required.';
    if (!fieldValue(values.recordId)) validationErrors.recordId = 'Record ID is required.';
    if (!fieldValue(values.categoryCode)) validationErrors.categoryCode = 'Select an escalation category.';
    if (!fieldValue(values.subjectCode)) validationErrors.subjectCode = 'Select an escalation subject.';
    if (!fieldValue(values.details)) {
        validationErrors.details = 'Detailed explanation is required.';
    } else if (detailsLength < DETAILS_MIN) {
        validationErrors.details = 'Detailed explanation must be at least 200 characters.';
    } else if (detailsLength > DETAILS_MAX) {
        validationErrors.details = `Detailed explanation must be ${DETAILS_MAX} characters or fewer.`;
    }

    return validationErrors;
}

function detailsProgressText(length) {
    if (length === 0) return `Minimum ${DETAILS_MIN} characters required — 0/${DETAILS_MAX}`;
    if (length < DETAILS_MIN) return `${DETAILS_MIN - length} more characters required — ${length}/${DETAILS_MAX}`;
    if (length <= DETAILS_MAX) return `Minimum length met — ${length}/${DETAILS_MAX}`;
    return `${length - DETAILS_MAX} characters over the maximum — ${length}/${DETAILS_MAX}`;
}

function buildCopyDetailsText(escalation) {
    return [
        `Escalation: ${escalation.escalationNumber}`,
        `Status: ${escalation.statusLabel}`,
        `Cx name: ${escalation.customerFullName}`,
        `Rec ID: ${escalation.recordId}`,
        `Account: ${escalation.accountNumber || 'N/A'}`,
        `Agent: ${escalation.agentName}`,
        `Category: ${escalation.categoryLabel}`,
        `Subject: ${escalation.subjectLabel}`,
        `Details: ${escalation.details}`,
        `Submitted: ${formatDateTime(escalation.submittedAt)}`
    ].join('\n');
}

function buildCoreCopyDetailsText(escalation) {
    return [
        `Cx name: ${escalation.customerFullName}`,
        `Rec ID: ${escalation.recordId}`,
        `Account: ${escalation.accountNumber || 'N/A'}`
    ].join('\n');
}

function unreadLabel(item) {
    const count = Number(item?.unreadCount || 0);
    const comments = Number(item?.unreadCommentCount || 0);
    const statuses = Number(item?.unreadStatusCount || 0);
    if (count === 1 && comments === 1) return '1 new comment';
    if (count === 1 && statuses === 1) return '1 new status update';
    if (comments === count && count > 1) return `${count} new comments`;
    if (statuses === count && count > 1) return `${count} new status updates`;
    return `${count} new update${count === 1 ? '' : 's'}`;
}

function InformationItem({ label, value }) {
    return (
        <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" display="block" fontWeight={700}>
                {label}:
            </Typography>
            <Typography variant="body2" sx={ESCALATION_TEXT_SX}>
                {value || 'N/A'}
            </Typography>
        </Box>
    );
}

function EscalationInformation({ escalation }) {
    return (
        <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}><InformationItem label="Customer" value={escalation.customerFullName} /></Grid>
            <Grid item xs={12} sm={6}><InformationItem label="Record ID" value={escalation.recordId} /></Grid>
            <Grid item xs={12} sm={6}><InformationItem label="Account" value={escalation.accountNumber || escalation.accountNumberMasked || 'N/A'} /></Grid>
            <Grid item xs={12} sm={6}><InformationItem label="Submitted by" value={escalation.agentName} /></Grid>
            <Grid item xs={12} sm={6}><InformationItem label="Submitted" value={formatDateTime(escalation.submittedAt)} /></Grid>
        </Grid>
    );
}

function EscalationFormDialog({ open, options, onClose, onCreated }) {
    const [values, setValues] = useState({
        customerFullName: '',
        recordId: '',
        accountNumber: '',
        categoryCode: '',
        subjectCode: '',
        details: ''
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const fieldContainersRef = useRef({});

    const selectedCategory = useMemo(
        () => (options?.categories || []).find((category) => category.code === values.categoryCode),
        [options, values.categoryCode]
    );

    useEffect(() => {
        if (!open) {
            setValues({
                customerFullName: '',
                recordId: '',
                accountNumber: '',
                categoryCode: '',
                subjectCode: '',
                details: ''
            });
            setError('');
            setFieldErrors({});
            setSaving(false);
        }
    }, [open]);

    const update = (field, value) => {
        setFieldErrors((previous) => ({ ...previous, [field]: '' }));
        setValues((previous) => ({
            ...previous,
            [field]: value,
            ...(field === 'categoryCode' ? { subjectCode: '' } : {})
        }));
    };

    const detailsLength = values.details.trim().length;
    const detailsHelper = detailsProgressText(detailsLength);

    const focusFirstInvalidField = (validationErrors) => {
        const fieldOrder = ['customerFullName', 'recordId', 'categoryCode', 'subjectCode', 'details'];
        const firstInvalidField = fieldOrder.find((field) => validationErrors[field]);
        const container = firstInvalidField ? fieldContainersRef.current[firstInvalidField] : null;
        if (!container) return;
        container.scrollIntoView({ behavior: 'smooth', block: 'center' });
        requestAnimationFrame(() => {
            container.querySelector('input, textarea, [role="combobox"]')?.focus();
        });
    };

    const handleSubmit = async () => {
        if (saving) return;
        const validationErrors = validateEscalationForm(values);
        setFieldErrors(validationErrors);
        if (Object.keys(validationErrors).length > 0) {
            setError('Please correct the highlighted fields before submitting.');
            focusFirstInvalidField(validationErrors);
            return;
        }

        setSaving(true);
        setError('');
        try {
            const response = await fetch('/api/escalations', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(values)
            });
            const data = await response.json();
            if (!response.ok || !data.success) {
                throw new Error(data.error || 'Failed to create escalation.');
            }
            onCreated(data.escalation, data.duplicateWarning || null);
        } catch (submitError) {
            setError(submitError.message || 'Failed to create escalation.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
            <DialogTitle>New Escalation</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2.5} sx={{ mt: 1 }}>
                    {error && <Alert severity="error">{error}</Alert>}
                    <Alert severity="warning">
                        {options?.sensitiveDataWarning || 'Do not include passwords, PINs, full payment card details, or identity documents.'}
                    </Alert>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6} ref={(node) => { fieldContainersRef.current.customerFullName = node; }}>
                            <TextField
                                label="Customer Full Name"
                                value={values.customerFullName}
                                onChange={(event) => update('customerFullName', event.target.value)}
                                required
                                fullWidth
                                error={Boolean(fieldErrors.customerFullName)}
                                helperText={fieldErrors.customerFullName || ' '}
                            />
                        </Grid>
                        <Grid item xs={12} md={6} ref={(node) => { fieldContainersRef.current.recordId = node; }}>
                            <TextField
                                label="Record ID"
                                value={values.recordId}
                                onChange={(event) => update('recordId', event.target.value)}
                                required
                                fullWidth
                                error={Boolean(fieldErrors.recordId)}
                                helperText={fieldErrors.recordId || ' '}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Account Number"
                                value={values.accountNumber}
                                onChange={(event) => update('accountNumber', event.target.value)}
                                helperText="Optional. Stored encrypted and shown only in authorized detail views."
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} md={6} ref={(node) => { fieldContainersRef.current.categoryCode = node; }}>
                            <FormControl fullWidth required error={Boolean(fieldErrors.categoryCode)}>
                                <InputLabel>Category</InputLabel>
                                <Select
                                    label="Category"
                                    value={values.categoryCode}
                                    onChange={(event) => update('categoryCode', event.target.value)}
                                >
                                    {(options?.categories || []).map((category) => (
                                        <MenuItem value={category.code} key={category.code}>
                                            {category.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <FormHelperText>{fieldErrors.categoryCode || ' '}</FormHelperText>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} ref={(node) => { fieldContainersRef.current.subjectCode = node; }}>
                            <FormControl fullWidth required disabled={!selectedCategory} error={Boolean(fieldErrors.subjectCode)}>
                                <InputLabel>Subject</InputLabel>
                                <Select
                                    label="Subject"
                                    value={values.subjectCode}
                                    onChange={(event) => update('subjectCode', event.target.value)}
                                >
                                    {(selectedCategory?.subjects || []).map((subject) => (
                                        <MenuItem value={subject.code} key={subject.code}>
                                            {subject.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <FormHelperText>{fieldErrors.subjectCode || ' '}</FormHelperText>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} ref={(node) => { fieldContainersRef.current.details = node; }}>
                            <TextField
                                label="Detailed Explanation"
                                value={values.details}
                                onChange={(event) => update('details', event.target.value)}
                                required
                                fullWidth
                                multiline
                                minRows={8}
                                helperText={fieldErrors.details ? `${fieldErrors.details} ${detailsHelper}` : detailsHelper}
                                error={Boolean(fieldErrors.details) || detailsLength > DETAILS_MAX}
                            />
                        </Grid>
                    </Grid>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button variant="contained" startIcon={<AddIcon />} onClick={handleSubmit} disabled={saving}>
                    {saving ? 'Submitting...' : 'Submit Escalation'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function DetailDialog({ open, escalationNumber, isAdmin, onClose, onChanged, onRead }) {
    const [loading, setLoading] = useState(false);
    const [detail, setDetail] = useState(null);
    const [comment, setComment] = useState('');
    const [status, setStatus] = useState('');
    const [deleteReason, setDeleteReason] = useState('');
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const [copyNotice, setCopyNotice] = useState(null);

    const markLoadedDetailRead = useCallback(async (loadedDetail) => {
        const cursor = loadedDetail?.readCursor;
        if (!escalationNumber || !cursor) return;
        try {
            const response = await fetch(`/api/escalations/${encodeURIComponent(escalationNumber)}/read`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(cursor)
            });
            const data = await response.json();
            if (response.ok && data.success && onRead) {
                onRead();
            }
        } catch (readError) {
            // Reading the thread remains available if the non-blocking read receipt fails.
        }
    }, [escalationNumber, onRead]);

    const loadDetail = useCallback(async () => {
        if (!escalationNumber) return;
        setLoading(true);
        setError('');
        try {
            const response = await fetch(`/api/escalations/${encodeURIComponent(escalationNumber)}`);
            const data = await response.json();
            if (!response.ok || !data.success) {
                throw new Error(data.error || 'Failed to load escalation.');
            }
            setDetail(data);
            setStatus(data.escalation?.status || '');
            markLoadedDetailRead(data);
        } catch (loadError) {
            setError(loadError.message || 'Failed to load escalation.');
        } finally {
            setLoading(false);
        }
    }, [escalationNumber, markLoadedDetailRead]);

    useEffect(() => {
        if (open) {
            loadDetail();
            setComment('');
            setDeleteReason('');
            setConfirmDelete(false);
            setCopyNotice(null);
        }
    }, [loadDetail, open]);

    const saveComment = async () => {
        setSaving(true);
        setError('');
        try {
            const response = await fetch(`/api/escalations/${encodeURIComponent(escalationNumber)}/comments`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ body: comment })
            });
            const data = await response.json();
            if (!response.ok || !data.success) {
                throw new Error(data.error || 'Failed to add comment.');
            }
            setDetail(data);
            setComment('');
            markLoadedDetailRead(data);
            onChanged();
        } catch (commentError) {
            setError(commentError.message || 'Failed to add comment.');
        } finally {
            setSaving(false);
        }
    };

    const saveStatus = async () => {
        setSaving(true);
        setError('');
        try {
            const response = await fetch(`/api/escalations/${encodeURIComponent(escalationNumber)}/status`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status })
            });
            const data = await response.json();
            if (!response.ok || !data.success) {
                throw new Error(data.error || 'Failed to update status.');
            }
            setDetail(data);
            markLoadedDetailRead(data);
            onChanged();
        } catch (statusError) {
            setError(statusError.message || 'Failed to update status.');
        } finally {
            setSaving(false);
        }
    };

    const softDelete = async () => {
        setSaving(true);
        setError('');
        try {
            const response = await fetch(`/api/escalations/${encodeURIComponent(escalationNumber)}`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ reason: deleteReason })
            });
            const data = await response.json();
            if (!response.ok || !data.success) {
                throw new Error(data.error || 'Failed to delete escalation.');
            }
            onChanged();
            onClose();
        } catch (deleteError) {
            setError(deleteError.message || 'Failed to delete escalation.');
        } finally {
            setSaving(false);
        }
    };

    const escalation = detail?.escalation;
    const commentLength = comment.trim().length;

    const copyDetails = async () => {
        try {
            await navigator.clipboard.writeText(buildCopyDetailsText(escalation));
            setCopyNotice({ severity: 'success', message: 'Escalation details copied.' });
        } catch (copyError) {
            setCopyNotice({ severity: 'error', message: 'Unable to copy escalation details. Please try again.' });
        }
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={ESCALATION_TEXT_SX}>
                {escalation ? `${escalation.escalationNumber} - ${escalation.subjectLabel}` : 'Escalation'}
            </DialogTitle>
            <DialogContent dividers>
                {loading && <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>}
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {copyNotice && <Alert severity={copyNotice.severity} sx={{ mb: 2 }}>{copyNotice.message}</Alert>}
                {escalation && (
                    <Stack spacing={3}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
                                <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" mb={1}>
                                        {statusChip(escalation.status)}
                                        <Chip size="small" variant="outlined" label={`Age ${ageLabel(escalation.submittedAt)}`} />
                                        <Chip size="small" variant="outlined" label={`Last activity ${formatDateTime(escalation.lastActivityAt)}`} />
                                        <Chip size="small" variant="outlined" label={`${escalation.commentCount} comments`} />
                                    </Stack>
                                    <Typography variant="h6" sx={ESCALATION_TEXT_SX}>{escalation.subjectLabel}</Typography>
                                    <Typography variant="body2" color="text.secondary" sx={ESCALATION_TEXT_SX}>{escalation.categoryLabel}</Typography>
                                    <EscalationInformation escalation={escalation} />
                                </Box>
                                {isAdmin && (
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                                        <Button variant="outlined" startIcon={<CopyIcon />} onClick={copyDetails}>
                                            Copy Details
                                        </Button>
                                        <FormControl size="small" sx={{ minWidth: 150 }}>
                                            <InputLabel>Status</InputLabel>
                                            <Select label="Status" value={status} onChange={(event) => setStatus(event.target.value)}>
                                                {STATUS_TABS.map((item) => (
                                                    <MenuItem key={item.code} value={item.code}>{item.label}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <Button variant="contained" onClick={saveStatus} disabled={saving || status === escalation.status}>
                                            Update
                                        </Button>
                                    </Stack>
                                )}
                            </Stack>
                        </Paper>

                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={ESCALATION_TEXT_SX}>
                                {escalation.categoryLabel}
                            </Typography>
                            <Typography variant="h6" gutterBottom sx={ESCALATION_TEXT_SX}>{escalation.subjectLabel}</Typography>
                            <Typography sx={ESCALATION_TEXT_SX}>{escalation.details}</Typography>
                        </Paper>

                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="h6" gutterBottom>Timeline</Typography>
                            <Stack spacing={2}>
                                {(detail.timeline || []).map((item) => (
                                    <Box key={item.id} sx={{ minWidth: 0, maxWidth: '100%' }}>
                                        <Typography variant="body2" fontWeight={700} sx={ESCALATION_TEXT_SX}>
                                            {item.type === 'status'
                                                ? (item.previousStatus
                                                    ? `Status changed from ${item.previousStatus} to ${item.newStatusLabel || item.newStatus}`
                                                    : `Escalation submitted by ${item.authorName}`)
                                                : `${item.authorName} (${item.authorRole})`}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {formatDateTime(item.createdAt)}
                                        </Typography>
                                        {item.body && (
                                            <Typography variant="body2" sx={{ ...ESCALATION_TEXT_SX, mt: 0.5 }}>
                                                {item.body}
                                            </Typography>
                                        )}
                                        <Divider sx={{ mt: 1.5 }} />
                                    </Box>
                                ))}
                            </Stack>
                        </Paper>

                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <TextField
                                label="Add comment"
                                value={comment}
                                onChange={(event) => setComment(event.target.value)}
                                fullWidth
                                multiline
                                minRows={3}
                                helperText={`${commentLength}/${COMMENT_MAX} characters. Comments are visible to both agent and admin.`}
                            />
                            <Box display="flex" justifyContent="flex-end" mt={1.5}>
                                <Button
                                    variant="contained"
                                    startIcon={<ForumIcon />}
                                    disabled={saving || commentLength < 1 || commentLength > COMMENT_MAX}
                                    onClick={saveComment}
                                >
                                    Add Comment
                                </Button>
                            </Box>
                        </Paper>

                        {isAdmin && (
                            <Paper variant="outlined" sx={{ p: 2, borderColor: 'error.light' }}>
                                {!confirmDelete ? (
                                    <Button color="error" startIcon={<DeleteIcon />} onClick={() => setConfirmDelete(true)}>
                                        Delete Escalation
                                    </Button>
                                ) : (
                                    <Stack spacing={1.5}>
                                        <Alert severity="warning">This is a soft delete. Agents will no longer see this escalation.</Alert>
                                        <TextField
                                            label="Deletion reason"
                                            value={deleteReason}
                                            onChange={(event) => setDeleteReason(event.target.value)}
                                            fullWidth
                                            multiline
                                            minRows={2}
                                        />
                                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                                            <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
                                            <Button color="error" variant="contained" onClick={softDelete} disabled={saving}>
                                                Confirm Delete
                                            </Button>
                                        </Stack>
                                    </Stack>
                                )}
                            </Paper>
                        )}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}

function EscalationList({ rows, onOpen, isAdmin, onCopyCoreDetails, copyingNumber }) {
    if (!rows.length) {
        return (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">No escalations found.</Typography>
            </Paper>
        );
    }

    return (
        <Stack spacing={1.5}>
            {rows.map((item) => (
                <Card key={item.escalationNumber} variant="outlined" sx={{ minWidth: 0, maxWidth: '100%' }}>
                    <CardContent>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
                            <Box sx={{ minWidth: 0 }}>
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" mb={1}>
                                    <Typography variant="h6">{item.escalationNumber}</Typography>
                                    {statusChip(item.status)}
                                    <Chip size="small" variant="outlined" label={`Age ${ageLabel(item.submittedAt)}`} />
                                    <Chip size="small" variant="outlined" label={`Last activity ${formatDateTime(item.lastActivityAt)}`} />
                                    <Chip size="small" variant="outlined" label={`${item.commentCount} comments`} />
                                    {item.hasUnread && (
                                        <Chip size="small" color="error" label={unreadLabel(item)} />
                                    )}
                                </Stack>
                                <Typography fontWeight={700} sx={ESCALATION_TEXT_SX}>{item.subjectLabel}</Typography>
                                <Typography variant="body2" color="text.secondary" sx={ESCALATION_TEXT_SX}>{item.categoryLabel}</Typography>
                                <EscalationInformation escalation={item} />
                            </Box>
                            <Stack
                                direction={{ xs: 'row', md: 'column' }}
                                spacing={1}
                                alignItems={{ xs: 'center', md: 'flex-end' }}
                                justifyContent={{ xs: 'flex-start', md: 'center' }}
                            >
                                {isAdmin && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<CopyIcon />}
                                        disabled={copyingNumber === item.escalationNumber}
                                        onClick={() => onCopyCoreDetails(item.escalationNumber)}
                                    >
                                        {copyingNumber === item.escalationNumber ? 'Copying...' : 'Copy Core Details'}
                                    </Button>
                                )}
                                <Tooltip title="Open escalation">
                                    <IconButton color="primary" onClick={() => onOpen(item.escalationNumber)}>
                                        <VisibilityIcon />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </Stack>
                    </CardContent>
                </Card>
            ))}
        </Stack>
    );
}

function EscalationsPage({
    availability = null,
    availabilityLoaded = false,
    onAvailabilityChange
}) {
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const isAdmin = Boolean(currentUser?.isAdmin);
    const canUseEscalations = Boolean(isAdmin || availability?.availableToCurrentUser);
    const [options, setOptions] = useState(null);
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState(null);
    const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 1 });
    const [status, setStatus] = useState('submitted');
    const [month, setMonth] = useState(currentVancouverMonth());
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [formOpen, setFormOpen] = useState(false);
    const [detailNumber, setDetailNumber] = useState('');
    const [duplicateWarning, setDuplicateWarning] = useState(null);
    const [adminConfig, setAdminConfig] = useState(null);
    const [configLoading, setConfigLoading] = useState(false);
    const [configSaving, setConfigSaving] = useState(false);
    const [configError, setConfigError] = useState('');
    const [unreadSummary, setUnreadSummary] = useState({
        totalUnreadEscalations: 0,
        totalUnreadUpdates: 0,
        items: [],
        hasMore: false
    });
    const [unreadExpanded, setUnreadExpanded] = useState(false);
    const [unreadLoading, setUnreadLoading] = useState(false);
    const [copyCoreNotice, setCopyCoreNotice] = useState(null);
    const [copyingNumber, setCopyingNumber] = useState('');
    const listRequestIdRef = useRef(0);

    const loadOptions = useCallback(async () => {
        if (!availabilityLoaded || !canUseEscalations) return;
        const response = await fetch('/api/escalations/options');
        const data = await response.json();
        if (response.ok && data.success) {
            setOptions(data);
        }
    }, [availabilityLoaded, canUseEscalations]);

    const loadEscalations = useCallback(async (nextPage = 1) => {
        if (!availabilityLoaded || !canUseEscalations) return;
        const requestId = listRequestIdRef.current + 1;
        listRequestIdRef.current = requestId;
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams({
                status,
                page: String(nextPage),
                limit: String(pagination.limit)
            });
            if (status !== 'submitted') params.set('month', month);
            if (search.trim()) params.set('search', search.trim());
            const endpoint = isAdmin ? '/api/escalations/admin' : '/api/escalations/mine';
            const response = await fetch(`${endpoint}?${params.toString()}`);
            const data = await response.json();
            if (requestId !== listRequestIdRef.current) return;
            if (!response.ok || !data.success) {
                throw new Error(data.error || 'Failed to load escalations.');
            }
            setRows(data.rows || []);
            setSummary(data.summary || null);
            setPagination(data.pagination || { page: nextPage, limit: 25, total: 0, totalPages: 1 });
        } catch (loadError) {
            if (requestId === listRequestIdRef.current) {
                setError(loadError.message || 'Failed to load escalations.');
            }
        } finally {
            if (requestId === listRequestIdRef.current) {
                setLoading(false);
            }
        }
    }, [availabilityLoaded, canUseEscalations, isAdmin, month, pagination.limit, search, status]);

    const loadUnreadSummary = useCallback(async (limit = 5) => {
        if (!availabilityLoaded || !canUseEscalations) return;
        setUnreadLoading(true);
        try {
            const response = await fetch(`/api/escalations/unread-summary?limit=${limit}`);
            const data = await response.json();
            if (!response.ok || !data.success) return;
            const nextSummary = {
                totalUnreadEscalations: Number(data.totalUnreadEscalations || 0),
                totalUnreadUpdates: Number(data.totalUnreadUpdates || 0),
                items: data.items || [],
                hasMore: Boolean(data.hasMore)
            };
            setUnreadSummary(nextSummary);
            window.dispatchEvent(new CustomEvent('escalations-unread-changed', { detail: nextSummary }));
        } catch (unreadError) {
            // Unread indicators are non-blocking and fail quietly.
        } finally {
            setUnreadLoading(false);
        }
    }, [availabilityLoaded, canUseEscalations]);

    const loadAdminConfig = useCallback(async () => {
        if (!isAdmin || !availabilityLoaded) return;
        setConfigLoading(true);
        setConfigError('');
        try {
            const response = await fetch('/api/escalations/admin/config');
            const data = await response.json();
            if (!response.ok || !data.success) {
                throw new Error(data.error || 'Failed to load Escalations configuration.');
            }
            setAdminConfig(data.config || null);
        } catch (loadError) {
            setConfigError(loadError.message || 'Failed to load Escalations configuration.');
        } finally {
            setConfigLoading(false);
        }
    }, [availabilityLoaded, isAdmin]);

    const updateAgentAccess = async (enabled) => {
        if (enabled && !window.confirm('Enabling this will immediately make Escalations available to all agents.')) {
            return;
        }
        if (!enabled && !window.confirm('Disable Escalations for agents? Existing records and history will be preserved.')) {
            return;
        }

        setConfigSaving(true);
        setConfigError('');
        try {
            const response = await fetch('/api/escalations/admin/config', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ agentAccessEnabled: enabled })
            });
            const data = await response.json();
            if (!response.ok || !data.success) {
                throw new Error(data.error || 'Failed to update Escalations configuration.');
            }
            setAdminConfig(data.config || null);
            if (onAvailabilityChange && data.availability) {
                onAvailabilityChange(data.availability);
            }
        } catch (saveError) {
            setConfigError(saveError.message || 'Failed to update Escalations configuration.');
        } finally {
            setConfigSaving(false);
        }
    };

    useEffect(() => {
        loadOptions();
    }, [loadOptions]);

    useEffect(() => {
        if (availabilityLoaded && canUseEscalations) {
            loadEscalations(1);
            loadUnreadSummary(5);
        }
    }, [availabilityLoaded, canUseEscalations, loadEscalations, loadUnreadSummary]);

    useEffect(() => {
        loadAdminConfig();
    }, [loadAdminConfig]);

    const handleTabChange = (event, nextStatus) => {
        listRequestIdRef.current += 1;
        setRows([]);
        setLoading(true);
        setStatus(nextStatus);
        setPagination((previous) => ({ ...previous, page: 1 }));
        setDuplicateWarning(null);
    };

    const handleCreated = (escalation, warning) => {
        setFormOpen(false);
        setDuplicateWarning(warning);
        setStatus('submitted');
        setSearch(escalation.escalationNumber);
        setPagination((previous) => ({ ...previous, page: 1 }));
        setDetailNumber(escalation.escalationNumber);
    };

    const refreshEscalationData = useCallback(() => {
        loadEscalations(pagination.page);
        loadUnreadSummary(unreadExpanded ? 25 : 5);
    }, [loadEscalations, loadUnreadSummary, pagination.page, unreadExpanded]);

    const copyCoreDetails = async (escalationNumber) => {
        if (!isAdmin || copyingNumber) return;
        setCopyingNumber(escalationNumber);
        setCopyCoreNotice(null);
        try {
            const response = await fetch(`/api/escalations/${encodeURIComponent(escalationNumber)}`);
            const data = await response.json();
            if (!response.ok || !data.success || !data.escalation) {
                throw new Error('Unable to load authorized escalation details.');
            }
            await navigator.clipboard.writeText(buildCoreCopyDetailsText(data.escalation));
            setCopyCoreNotice({ severity: 'success', message: 'Core escalation details copied.' });
        } catch (copyError) {
            setCopyCoreNotice({
                severity: 'error',
                message: 'Unable to copy core escalation details. Open the escalation and try again.'
            });
        } finally {
            setCopyingNumber('');
        }
    };

    const countCards = isAdmin && summary ? [
        { label: 'Submitted', value: getCount(summary, 'submitted'), color: 'info' },
        { label: `Pending ${summary.currentMonth || month}`, value: summary.currentMonthCounts?.pending || 0, color: 'warning' },
        { label: `Complete ${summary.currentMonth || month}`, value: summary.currentMonthCounts?.completed || 0, color: 'success' },
        { label: 'Oldest Submitted', value: summary.oldestSubmittedAt ? ageLabel(summary.oldestSubmittedAt) : 'None', color: 'default' }
    ] : [];

    if (!availabilityLoaded) {
        return (
            <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress />
            </Box>
        );
    }

    if (!canUseEscalations) {
        return (
            <Paper sx={{ p: 3, maxWidth: 720 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                    Escalations are not currently available. Please contact management if you need assistance.
                </Alert>
                <Button variant="contained" onClick={() => navigate('/dashboard')}>
                    Back to Dashboard
                </Button>
            </Paper>
        );
    }

    return (
        <Box>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={2} mb={3}>
                <Box>
                    <Typography variant="h4" fontWeight={700}>Escalations</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Private order and response issue tracking between agents and management.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button variant="outlined" startIcon={<RefreshIcon />} onClick={refreshEscalationData}>
                        Refresh
                    </Button>
                    {!isAdmin && (
                        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setFormOpen(true)}>
                            New Escalation
                        </Button>
                    )}
                </Stack>
            </Stack>

            {isAdmin && (
                <Paper sx={{ p: 2.5, mb: 2, borderLeft: '4px solid', borderColor: adminConfig?.agentAccessEnabled ? 'success.main' : 'warning.main' }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                        <Box>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" mb={0.75}>
                                <Typography variant="h6" fontWeight={700}>Agent Access</Typography>
                                <Chip
                                    size="small"
                                    color={adminConfig?.agentAccessEnabled ? 'success' : 'warning'}
                                    label={adminConfig?.agentAccessEnabled ? 'Enabled' : 'Disabled'}
                                />
                                {configLoading && <CircularProgress size={18} />}
                            </Stack>
                            <Typography variant="body2" color="text.secondary">
                                {adminConfig?.updatedAt
                                    ? `Last updated ${formatDateTime(adminConfig.updatedAt)}${adminConfig.updatedByName ? ` by ${adminConfig.updatedByName}` : ''}.`
                                    : 'Disabled by default. Administrators can test Escalations before agent rollout.'}
                            </Typography>
                            {configError && <Alert severity="error" sx={{ mt: 1 }}>{configError}</Alert>}
                        </Box>
                        <Stack direction="row" spacing={1}>
                            <Button
                                variant={adminConfig?.agentAccessEnabled ? 'outlined' : 'contained'}
                                color="success"
                                disabled={configSaving || Boolean(adminConfig?.agentAccessEnabled)}
                                onClick={() => updateAgentAccess(true)}
                            >
                                Enable for Agents
                            </Button>
                            <Button
                                variant={adminConfig?.agentAccessEnabled ? 'contained' : 'outlined'}
                                color="warning"
                                disabled={configSaving || !adminConfig?.agentAccessEnabled}
                                onClick={() => updateAgentAccess(false)}
                            >
                                Disable for Agents
                            </Button>
                        </Stack>
                    </Stack>
                </Paper>
            )}

            {unreadSummary.totalUnreadEscalations > 0 && (
                <Paper sx={{ p: 2.5, mb: 2, borderLeft: '4px solid', borderColor: 'error.main' }}>
                    <Stack spacing={1.5}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <NotificationsIcon color="error" />
                            <Typography variant="h6" fontWeight={700}>Unread Escalation Updates</Typography>
                            <Chip size="small" color="error" label={unreadSummary.totalUnreadEscalations} />
                            {unreadLoading && <CircularProgress size={18} />}
                        </Stack>
                        {(unreadSummary.items || []).map((item, index) => (
                            <React.Fragment key={item.escalationNumber}>
                                {index > 0 && <Divider />}
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    spacing={1.5}
                                    justifyContent="space-between"
                                    alignItems={{ xs: 'stretch', sm: 'center' }}
                                >
                                    <Box sx={{ minWidth: 0 }}>
                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                            <Typography variant="subtitle2" fontWeight={700} sx={ESCALATION_TEXT_SX}>
                                                {item.escalationNumber}
                                            </Typography>
                                            <Chip size="small" color="error" variant="outlined" label={unreadLabel(item)} />
                                        </Stack>
                                        <Typography variant="body2" sx={ESCALATION_TEXT_SX}>{item.subject}</Typography>
                                        {isAdmin && item.agentName && (
                                            <Typography variant="caption" color="text.secondary" sx={ESCALATION_TEXT_SX}>
                                                {item.agentName}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Button size="small" variant="outlined" onClick={() => setDetailNumber(item.escalationNumber)}>
                                        View
                                    </Button>
                                </Stack>
                            </React.Fragment>
                        ))}
                        {(unreadSummary.hasMore || unreadExpanded) && (
                            <Box>
                                <Button
                                    size="small"
                                    onClick={() => {
                                        const nextExpanded = !unreadExpanded;
                                        setUnreadExpanded(nextExpanded);
                                        loadUnreadSummary(nextExpanded ? 25 : 5);
                                    }}
                                >
                                    {unreadExpanded ? 'Show fewer unread' : 'View all unread'}
                                </Button>
                            </Box>
                        )}
                    </Stack>
                </Paper>
            )}

            {isAdmin && (
                <Grid container spacing={2} mb={2}>
                    {countCards.map((card) => (
                        <Grid item xs={12} sm={6} md={3} key={card.label}>
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="caption" color="text.secondary">{card.label}</Typography>
                                <Typography variant="h5" color={card.color === 'default' ? 'text.primary' : `${card.color}.main`} fontWeight={700}>
                                    {card.value}
                                </Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            )}

            {duplicateWarning && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {duplicateWarning.message}
                    {duplicateWarning.type === 'own' && duplicateWarning.escalationNumber && (
                        <Button size="small" sx={{ ml: 1 }} onClick={() => setDetailNumber(duplicateWarning.escalationNumber)}>
                            Open {duplicateWarning.escalationNumber}
                        </Button>
                    )}
                </Alert>
            )}

            {copyCoreNotice && (
                <Alert severity={copyCoreNotice.severity} sx={{ mb: 2 }} onClose={() => setCopyCoreNotice(null)}>
                    {copyCoreNotice.message}
                </Alert>
            )}

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Paper sx={{ mb: 2 }}>
                <Tabs value={status} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
                    {STATUS_TABS.map((tab) => (
                        <Tab
                            key={tab.code}
                            value={tab.code}
                            label={`${tab.label} (${getTabCount(summary, tab.code)})`}
                        />
                    ))}
                </Tabs>
            </Paper>

            <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={status === 'submitted' ? 10 : 7}>
                        <TextField
                            label={isAdmin ? 'Search escalation number, agent, Record ID, or customer' : 'Search escalation number, Record ID, or customer'}
                            value={search}
                            onChange={(event) => {
                                listRequestIdRef.current += 1;
                                setRows([]);
                                setLoading(true);
                                setSearch(event.target.value);
                                setPagination((previous) => ({ ...previous, page: 1 }));
                            }}
                            fullWidth
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="action" />
                                    </InputAdornment>
                                )
                            }}
                            helperText={exactEscalationSearch(search) ? 'Exact escalation number search ignores the selected tab/month.' : 'Search applies within the selected tab and month.'}
                        />
                    </Grid>
                    {status !== 'submitted' && (
                        <Grid item xs={12} md={3}>
                            <TextField
                                label="Month"
                                type="month"
                                value={month}
                                onChange={(event) => {
                                    listRequestIdRef.current += 1;
                                    setRows([]);
                                    setLoading(true);
                                    setMonth(event.target.value || currentVancouverMonth());
                                    setPagination((previous) => ({ ...previous, page: 1 }));
                                }}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                    )}
                    <Grid item xs={12} md={2}>
                        <Button fullWidth variant="outlined" onClick={() => loadEscalations(1)}>
                            Apply
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {loading ? (
                <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
            ) : (
                <EscalationList
                    rows={rows}
                    onOpen={setDetailNumber}
                    isAdmin={isAdmin}
                    onCopyCoreDetails={copyCoreDetails}
                    copyingNumber={copyingNumber}
                />
            )}

            <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} flexWrap="wrap" gap={1}>
                <Typography variant="body2" color="text.secondary">
                    Showing page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
                </Typography>
                <Pagination
                    count={pagination.totalPages}
                    page={pagination.page}
                    onChange={(event, nextPage) => loadEscalations(nextPage)}
                    color="primary"
                />
            </Box>

            <EscalationFormDialog
                open={formOpen}
                options={options}
                onClose={() => setFormOpen(false)}
                onCreated={handleCreated}
            />

            <DetailDialog
                open={Boolean(detailNumber)}
                escalationNumber={detailNumber}
                isAdmin={isAdmin}
                onClose={() => setDetailNumber('')}
                onChanged={refreshEscalationData}
                onRead={refreshEscalationData}
            />
        </Box>
    );
}

export default EscalationsPage;
